"use client"
import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import socket from "@/socket";
import { Socket } from "socket.io-client";
import toast from "react-hot-toast";

type SocketContextType = {
    socket: Socket;
};

const SocketContext = createContext<SocketContextType | undefined>(undefined);

export const useSocket = (): SocketContextType => {
    const context = useContext(SocketContext);
    if (!context) {
        throw new Error("useSocket must be used within a SocketProvider");
    }
    return context;
};

export const SocketProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [isConnected, setIsConnected] = useState(socket.connected);

    useEffect(() => {
        // Проверяем, подключен ли сокет уже (на случай, если подключение произошло до установки обработчиков)
        if (socket.connected) {
            setIsConnected(true);
            console.log("Socket already connected!");
        }

        const handleConnect = () => {
            setIsConnected(true);
            console.log("You are online!");
            toast.success("You are online!");
            
            // Настраиваем отправку heartbeat каждые 8 секунд
            const heartbeatInterval = setInterval(() => {
                if (socket.connected) {
                    socket.emit('heartbeat');
                } else {
                    clearInterval(heartbeatInterval);
                }
            }, 8000); // 8 секунд
            
            // Сохраняем interval для очистки
            (socket as any)._heartbeatInterval = heartbeatInterval;
        };

        const handleDisconnect = () => {
            setIsConnected(false);
            console.log("You are offline!");
            toast.error("You are offline!");
            
            // Останавливаем heartbeat
            if ((socket as any)._heartbeatInterval) {
                clearInterval((socket as any)._heartbeatInterval);
            }
        };

        // Подписываемся на события
        socket.on("connect", handleConnect);
        socket.on("disconnect", handleDisconnect);

        // Если сокет еще не подключен и есть токен, подключаемся явно
        if (!socket.connected && typeof window !== 'undefined') {
            const token = localStorage.getItem('token');
            if (token) {
                // Auth уже настроен через функцию в socket/index.ts
                // Просто подключаемся
                console.log("Connecting socket with token...");
                socket.connect();
            } else {
                console.warn("No token found, socket will not connect");
            }
        }

        return () => {
            socket.off("connect", handleConnect);
            socket.off("disconnect", handleDisconnect);
        };
    }, []);

    const contextValue = useMemo(() => ({ socket }), [isConnected]);

    return <SocketContext.Provider value={contextValue}>{children}</SocketContext.Provider>;
};
